import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ShieldCheck, ScanLine, Truck, BellRing, FileCheck2, ArrowRight,
  Check, Lock, Clock, Building2,
} from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1648479968388-d1265ffbbc38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwbmlnaHR8ZW58MHx8fGJsYWNrX2FuZF93aGl0ZXwxNzkxNTk5NDYxfDA&ixlib=rb-4.1.0&q=85';

const FEATURES = [
  { icon: Lock, title: 'Compliance gating', desc: 'Sequential onboarding with approval gates keeps drivers off the road until every credential checks out.' },
  { icon: ScanLine, title: 'OCR document scanning', desc: 'Scan CDLs, medical cards and clearinghouse docs. We auto-fill driver data and set expiries for you.' },
  { icon: Truck, title: 'Fleet management', desc: 'Track power units and trailers, assignments, maintenance and registrations in one place.' },
  { icon: BellRing, title: 'Automated expiry alerts', desc: 'Daily background scans and email digests warn you before any credential lapses.' },
  { icon: ShieldCheck, title: 'Audit-ready mode', desc: 'A clean, read-only inspector view puts every document and record at your fingertips during a DOT audit.' },
  { icon: FileCheck2, title: 'Smart dashboard', desc: 'FMCSA credentials, compliance trends and a unified notifications inbox at a glance.' },
];

const PLANS = [
  { key: 'basic', name: 'Basic', price: 0, tagline: 'For getting started', limits: ['Up to 10 drivers', 'Up to 10 trucks', 'Document scanning & expiry alerts', 'Audit-ready mode'], highlight: false },
  { key: 'corporate', name: 'Corporate', price: 100, tagline: 'For growing fleets', limits: ['Unlimited drivers', 'Unlimited trucks', 'Everything in Basic', 'Priority compliance automation'], highlight: true },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[hsl(var(--foreground))] text-white">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[hsl(var(--foreground))]/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:px-6">
          <div className="[&_*]:!text-white"><Logo /></div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" className="text-white hover:bg-white/10 hover:text-white" onClick={() => navigate('/login')} data-testid="landing-signin-button">Sign in</Button>
            <Button className="bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90" onClick={() => navigate('/signup')} data-testid="landing-start-button">Start your company</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(1000px_circle_at_15%_-10%,rgba(245,179,1,0.20),transparent_55%),radial-gradient(900px_circle_at_95%_10%,rgba(37,99,235,0.28),transparent_55%)]" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 py-16 md:grid-cols-2 md:px-6 md:py-24">
          <div>
            <Badge className="mb-5 border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/15" data-testid="landing-badge">DOT & FMCSA compliance, simplified</Badge>
            <h1 className="font-display text-4xl font-bold leading-tight tracking-tight md:text-6xl">
              Keep your fleet <span className="text-[hsl(var(--gold))]">road-legal</span>, automatically.
            </h1>
            <p className="mt-5 max-w-lg text-base text-white/70 md:text-lg">
              Manage drivers, trucks and documents with smart compliance gating, OCR scanning and automated expiration alerts — built for US trucking companies.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90" onClick={() => navigate('/signup')} data-testid="landing-hero-cta">
                Start your company <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white" onClick={() => navigate('/login')} data-testid="landing-hero-signin">Sign in</Button>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/60">
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-[hsl(var(--gold))]" /> Free Basic plan</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-[hsl(var(--gold))]" /> Invite your team</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-[hsl(var(--gold))]" /> Audit-ready in minutes</span>
            </div>
          </div>
          <div className="relative">
            <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
              <img src={HERO_IMG} alt="Semi truck on the highway at night" className="h-[340px] w-full object-cover md:h-[440px]" loading="eager" />
            </div>
            <Card className="absolute -bottom-5 left-4 flex items-center gap-3 border-0 bg-white px-4 py-3 text-[hsl(var(--foreground))] shadow-xl md:left-6">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--success))]/15"><ShieldCheck className="h-5 w-5 text-[hsl(var(--success))]" /></div>
              <div className="leading-tight"><div className="text-sm font-semibold">100% compliant</div><div className="text-xs text-muted-foreground">0 expired credentials</div></div>
            </Card>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white py-16 text-[hsl(var(--foreground))] md:py-24">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-3xl font-bold md:text-4xl">Everything you need to stay compliant</h2>
            <p className="mt-3 text-muted-foreground">From the first invite to a surprise DOT audit, NuxFleet keeps your records clean and your trucks moving.</p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <Card key={f.title} className="group p-6 transition-shadow hover:shadow-lg" data-testid={`landing-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]"><Icon className="h-5 w-5" /></div>
                  <h3 className="font-display text-lg font-semibold">{f.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="bg-[hsl(var(--background))] py-16 text-[hsl(var(--foreground))] md:py-24">
        <div className="mx-auto max-w-5xl px-4 md:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-3xl font-bold md:text-4xl">Simple, honest pricing</h2>
            <p className="mt-3 text-muted-foreground">Start free. Upgrade when your fleet grows. No surprises.</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {PLANS.map((p) => (
              <Card key={p.key} className={`relative p-7 ${p.highlight ? 'border-[hsl(var(--gold))] shadow-xl ring-1 ring-[hsl(var(--gold))]/40' : ''}`} data-testid={`landing-plan-${p.key}`}>
                {p.highlight && <Badge className="absolute -top-3 right-6 bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]">Most popular</Badge>}
                <div className="text-sm font-medium text-muted-foreground">{p.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-bold">${p.price}</span>
                  <span className="text-muted-foreground">/mo</span>
                </div>
                <div className="mt-1 text-sm text-muted-foreground">{p.tagline}</div>
                <ul className="mt-5 space-y-2.5">
                  {p.limits.map((l) => (
                    <li key={l} className="flex items-start gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--success))]" />{l}</li>
                  ))}
                </ul>
                <Button className={`mt-6 w-full ${p.highlight ? 'bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90' : ''}`} variant={p.highlight ? 'default' : 'outline'} onClick={() => navigate('/signup')} data-testid={`landing-plan-cta-${p.key}`}>
                  Get started
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="relative overflow-hidden bg-[hsl(var(--foreground))] py-16 md:py-20">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(700px_circle_at_50%_0%,rgba(245,179,1,0.18),transparent_60%)]" />
        <div className="relative mx-auto max-w-3xl px-4 text-center md:px-6">
          <Building2 className="mx-auto mb-4 h-10 w-10 text-[hsl(var(--gold))]" />
          <h2 className="font-display text-3xl font-bold md:text-4xl">Set up your company in minutes</h2>
          <p className="mt-3 text-white/70">Create your workspace, invite your team, and let NuxFleet handle the compliance paperwork.</p>
          <Button size="lg" className="mt-7 bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90" onClick={() => navigate('/signup')} data-testid="landing-footer-cta">Start your company <ArrowRight className="ml-2 h-4 w-4" /></Button>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-[hsl(var(--foreground))] py-8 text-center text-sm text-white/50">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-2 px-4">
          <div className="[&_*]:!text-white/80"><Logo /></div>
          <p className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Built by ServoApex Engineering Ltd. © 2026</p>
        </div>
      </footer>
    </div>
  );
}
